import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/global/base/button.tsx';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/global/base/select.tsx';

type PaginationProps = {
    pageIndex: number;
    pageSize: number;
    totalRows: number;
    startRow: number;
    endRow: number;
    pageSizes: string[];
    canPreviousPage: boolean;
    canNextPage: boolean;
    onPreviousPage: () => void;
    onNextPage: () => void;
    onPageSizeChange: (size: number) => void;
    tableSlug?: string;
};

const Pagination: React.FC<PaginationProps> = ({
    pageIndex,
    pageSize,
    totalRows,
    startRow,
    endRow,
    pageSizes,
    canPreviousPage,
    canNextPage,
    onPreviousPage,
    onNextPage,
    onPageSizeChange,
    tableSlug = 'data-table'
}) => {
    return (
        <div
            className="flex items-center justify-between px-4 py-3"
            data-testid={`${tableSlug}-pagination`}
        >
            <div className="flex space-x-7 text-left text-muted-foreground items-center">
                {/* Page Size Selector */}
                <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => onPageSizeChange(Number(value))}
                >
                    <SelectTrigger data-testid={`${tableSlug}-pagination-row-count-select`}>
                        <SelectValue placeholder={pageSize} />
                    </SelectTrigger>
                    <SelectContent>
                        {pageSizes.map((size) => (
                            <SelectItem
                                key={size}
                                value={size}
                                data-testid={`${tableSlug}-pagination-page-size-${size}`}
                            >
                                {size}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div data-testid={`${tableSlug}-pagination-summary`}>
                    {totalRows > 0
                        ? `Displaying ${startRow}-${endRow} of ${totalRows} records`
                        : 'No records available'}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Button
                    data-testid={`${tableSlug}-pagination-previous-button`}
                    variant="ghost"
                    onClick={onPreviousPage}
                    disabled={!canPreviousPage}
                    className="px-2 py-1 disabled:opacity-50"
                >
                    <ChevronLeft />
                </Button>
                <Button
                    data-testid={`${tableSlug}-pagination-page-index`}
                    variant="complement"
                >
                    {pageIndex + 1}
                </Button>
                <Button
                    data-testid={`${tableSlug}-pagination-next-button`}
                    variant="ghost"
                    onClick={onNextPage}
                    disabled={!canNextPage}
                    className="px-2 py-1 disabled:opacity-50"
                >
                    <ChevronRight />
                </Button>
            </div>
        </div>
    );
};

export default Pagination;
